import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ClipboardText,
  Command,
  BookOpen,
  Robot,
  Barcode,
  Clock,
  Users,
  Briefcase,
  Warning,
  CaretRight,
  ArrowsClockwise,
  Info,
  Lightning,
  MagnifyingGlass,
  FunnelSimple,
  ArrowLeft,
  User,
  CaretDown,
} from '@phosphor-icons/react';

// Icon mapping
const ICON_MAP = {
  workorder: Briefcase,
  crew: Users,
  canvass: ClipboardText,
  log: BookOpen,
  safety: Robot,
  timesheet: Clock,
  tool: Barcode,
  user: User,
  info: Info,
};

// App color mapping
const APP_COLORS = {
  pcc: 'text-blue-600 bg-blue-100',
  quickcanvass: 'text-emerald-600 bg-emerald-100',
  stationlogbook: 'text-amber-600 bg-amber-100',
  askstewie: 'text-violet-600 bg-violet-100',
  timesheets: 'text-orange-600 bg-orange-100',
  rfid: 'text-slate-600 bg-slate-100',
  getgridgate: 'text-primary bg-primary/10',
};

const APP_LABELS = {
  pcc: 'Command Center',
  quickcanvass: 'Quick Canvass',
  stationlogbook: 'Station Log',
  askstewie: 'AskStewie',
  timesheets: 'Timesheets',
  rfid: 'RFID',
  getgridgate: 'GETGRIDGATE',
};

const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return then.toLocaleDateString();
};

export const ActivityPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState({ info: 0, warning: 0, urgent: 0, total: 0 });
  
  // Filters
  const [appFilter, setAppFilter] = useState(searchParams.get('app') || 'all');
  const [severityFilter, setSeverityFilter] = useState(searchParams.get('severity') || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchFeed = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true);
      setCursor(null);
    } else {
      setLoadingMore(true);
    }
    
    try {
      let url = `/hub/feed?limit=30`;
      if (appFilter !== 'all') url += `&app_filter=${appFilter}`;
      if (severityFilter !== 'all') url += `&severity=${severityFilter}`;
      if (!reset && cursor) url += `&cursor=${cursor}`;
      
      const response = await api.get(url);
      const newItems = response.data?.items || [];
      
      if (reset) {
        setItems(newItems);
      } else {
        setItems(prev => [...prev, ...newItems]);
      }
      
      setCursor(response.data?.next_cursor);
      setHasMore(response.data?.has_more || false);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [api, appFilter, severityFilter, cursor]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/hub/feed/summary');
      setSummary(response.data || { info: 0, warning: 0, urgent: 0, total: 0 });
    } catch (error) {
      console.error('Failed to fetch feed summary:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchFeed(true);
    fetchSummary();
  }, [appFilter, severityFilter]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (appFilter !== 'all') params.set('app', appFilter);
    if (severityFilter !== 'all') params.set('severity', severityFilter);
    setSearchParams(params, { replace: true });
  }, [appFilter, severityFilter, setSearchParams]);

  const handleItemClick = (item) => {
    if (item.links && item.links.length > 0) {
      const primaryLink = item.links[0];
      if (primaryLink.url.startsWith('http')) {
        window.open(primaryLink.url, '_blank');
      } else {
        navigate(primaryLink.url);
      }
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'urgent':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Urgent</Badge>;
      case 'warning':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Warning</Badge>;
      default:
        return null;
    }
  };

  // Filter items by search query (client-side)
  const filteredItems = searchQuery
    ? items.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : items;

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="activity-page">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-4 -ml-2"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Hub
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Lightning size={28} weight="fill" className="text-primary" />
              Activity Feed
            </h1>
            <p className="text-slate-600 mt-1">
              Cross-app activity across GETGRIDGATE
            </p>
          </div>
          
          <div className="flex gap-2">
            {summary.warning > 0 && (
              <Badge className="bg-amber-100 text-amber-700">
                {summary.warning} Warning{summary.warning > 1 ? 's' : ''}
              </Badge>
            )}
            {summary.urgent > 0 && (
              <Badge className="bg-red-100 text-red-700">
                {summary.urgent} Urgent
              </Badge>
            )}
            <Badge variant="outline">{summary.total} events (24h)</Badge>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-600 mb-1 block">Search</label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="activity-search"
                />
              </div>
            </div>
            
            {/* App Filter */}
            <div className="w-48">
              <label className="text-sm font-medium text-slate-600 mb-1 block">App</label>
              <Select value={appFilter} onValueChange={setAppFilter}>
                <SelectTrigger data-testid="activity-app-filter">
                  <SelectValue placeholder="All Apps" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Apps</SelectItem>
                  <SelectItem value="pcc">Command Center</SelectItem>
                  <SelectItem value="quickcanvass">Quick Canvass</SelectItem>
                  <SelectItem value="stationlogbook">Station Log</SelectItem>
                  <SelectItem value="askstewie">AskStewie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Severity Filter */}
            <div className="w-40">
              <label className="text-sm font-medium text-slate-600 mb-1 block">Severity</label>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger data-testid="activity-severity-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Refresh */}
            <Button 
              variant="outline" 
              onClick={() => { fetchFeed(true); fetchSummary(); }}
              disabled={loading}
            >
              <ArrowsClockwise size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity List */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
          <CardDescription>
            Showing {filteredItems.length} of {items.length} activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Lightning size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No activities found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredItems.map((item, index) => {
                const IconComponent = ICON_MAP[item.iconKey] || Info;
                const appColor = APP_COLORS[item.appKey] || APP_COLORS.getgridgate;
                
                return (
                  <div
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className={`
                      flex items-start gap-4 p-4 rounded-lg cursor-pointer border
                      hover:bg-slate-50 hover:border-slate-300 transition-all group
                      ${item.severity === 'warning' ? 'bg-amber-50/50 border-amber-200' : ''}
                      ${item.severity === 'urgent' ? 'bg-red-50/50 border-red-200' : 'border-slate-100'}
                    `}
                    data-testid={`activity-item-${index}`}
                  >
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${appColor}`}>
                      <IconComponent size={20} weight="fill" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-slate-900">
                              {item.title}
                            </p>
                            {getSeverityBadge(item.severity)}
                          </div>
                          {item.subtitle && (
                            <p className="text-sm text-slate-600 mt-1">
                              {item.subtitle}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            {item.actor?.name && (
                              <span className="flex items-center gap-1">
                                <User size={12} />
                                {item.actor.name}
                              </span>
                            )}
                            <span title={new Date(item.ts).toLocaleString()}>
                              {formatRelativeTime(item.ts)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <Badge variant="outline" className={`text-xs ${appColor} border-0`}>
                            {APP_LABELS[item.appKey] || item.appKey}
                          </Badge>
                          {item.links && item.links.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                            >
                              {item.links[0].label}
                              <CaretRight size={14} className="ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Load More */}
          {hasMore && (
            <div className="mt-6 text-center">
              <Button 
                variant="outline" 
                onClick={() => fetchFeed(false)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <ArrowsClockwise size={16} className="mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <CaretDown size={16} className="mr-2" />
                    Load More
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityPage;
